import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Building2,
  KeyRound,
  FileClock,
  FileBarChart,
  Users,
  Monitor,
  Settings,
  Sparkles,
  Radio,
  History,
  Bell,
  X,
  MapPin,
} from 'lucide-react';
import { haptics } from '../lib/haptics';
import { sounds } from '../lib/sounds';

interface SidebarProps {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export function Sidebar({ mobileOpen, onCloseMobile }: SidebarProps) {
  const { user, remainingDays } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const superAdminNav = [
    { to: '/superadmin', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
    { to: '/superadmin/organizations', label: 'Organizations', icon: <Building2 className="w-4 h-4" /> },
    { to: '/superadmin/licenses', label: 'Licenses', icon: <KeyRound className="w-4 h-4" /> },
    { to: '/superadmin/audit', label: 'Audit Logs', icon: <FileClock className="w-4 h-4" /> },
  ];

  const adminNav = [
    { to: '/admin', label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
    { to: '/admin/live', label: 'Live Monitor', icon: <Radio className="w-4 h-4 text-emerald-500" /> },
    { to: '/admin/floor-plan', label: 'Floor Plan', icon: <MapPin className="w-4 h-4 text-violet-500" />, hideOnMobile: true },
    { to: '/admin/activity', label: 'Activity History', icon: <History className="w-4 h-4" /> },
    { to: '/admin/reports', label: 'Reports', icon: <FileBarChart className="w-4 h-4 text-blue-500" /> },
    { to: '/admin/users', label: 'Users', icon: <Users className="w-4 h-4" /> },
    { to: '/admin/devices', label: 'Devices', icon: <Monitor className="w-4 h-4" /> },
    { to: '/admin/notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { to: '/admin/settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
  ];

  const navItems = isSuperAdmin ? superAdminNav : adminNav;

  const NavContent = (
    <>
      <div className="space-y-1">
        <div className="flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
          <span>{isSuperAdmin ? 'Platform Admin' : 'Workstation Monitoring'}</span>
          {onCloseMobile && (
            <button
              onClick={() => {
                haptics.tap();
                onCloseMobile();
              }}
              className="lg:hidden p-1 text-zinc-400 hover:text-black dark:hover:text-white rounded-lg ios-press"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/admin' || item.to === '/superadmin'}
            onClick={() => {
              haptics.selection();
              sounds.playTapSound();
              onCloseMobile?.();
            }}
            className={({ isActive }) =>
              `${(item as any).hideOnMobile ? 'hidden lg:flex' : 'flex'} items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold ios-press transition-all ${
                isActive
                  ? 'bg-white text-zinc-900 shadow-sm border border-black/5 dark:bg-zinc-800/90 dark:text-white dark:border-white/10'
                  : 'text-zinc-600 dark:text-zinc-400 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-zinc-800/50'
              }`
            }
          >
            {item.icon}
            <span className="text-sm lg:text-xs">{item.label}</span>
          </NavLink>
        ))}
      </div>

      {!isSuperAdmin && (
        <div className="p-3.5 rounded-2xl bg-white/70 dark:bg-zinc-900/60 border border-black/[0.06] dark:border-white/[0.08] text-xs space-y-2 mt-4 shadow-sm">
          <div className="flex items-center gap-2 text-zinc-700 dark:text-zinc-300 font-semibold">
            <Sparkles className="w-3.5 h-3.5 text-pulse-blue" />
            <span>Organization License</span>
          </div>

          <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
            {user?.organization?.subscription?.isLifetime ? (
              <span className="text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider">
                LIFETIME LICENSE
              </span>
            ) : remainingDays !== null ? (
              <span>
                <strong className="text-zinc-900 dark:text-white">{remainingDays} days</strong> remaining
              </span>
            ) : (
              <span className="text-amber-500 font-medium">Active</span>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Desktop Sidebar (visible on lg and above) */}
      <aside className="hidden lg:flex w-60 border-r border-black/[0.06] dark:border-white/10 bg-white/50 dark:bg-[#0A0A0E]/80 backdrop-blur-xl flex-col justify-between p-3 min-h-[calc(100vh-3.5rem)] shrink-0 transition-colors">
        {NavContent}
      </aside>

      {/* Mobile Drawer Slide-over (visible on mobile when open) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={onCloseMobile}
          />

          {/* Drawer panel */}
          <div className="fixed inset-y-0 left-0 w-72 max-w-[82vw] bg-[#F2F2F7] dark:bg-[#0C0C0E] border-r border-black/10 dark:border-zinc-800 p-4 flex flex-col justify-between shadow-2xl z-50 overflow-y-auto">
            {NavContent}
          </div>
        </div>
      )}
    </>
  );
}
